import { ClosableProps, ModalContextShape } from 'components/no-ui/context/modal-provider'
import { createElement, useContext, useMemo, useState } from 'react'
import { ModalContext } from './context/app'

/**
 * A simple interface to open/close a non-prop-reactive modal.
 *
 * If you need changes in props to update your component, consider `useModal` which requires manually inserting the ReactNode.
 *
 * returns a bound function to open the modal and a function to close it
 */
export const useSimpleModal = (): Exclude<ModalContextShape, undefined> => {
    const c = useContext(ModalContext)
    if (!c) {
        throw new Error('no modal context provider found')
    }
    return c
}

type ModalType<P extends ClosableProps> = React.ComponentType<Omit<P, keyof ClosableProps>>
/**
 * A thin wrapper to open/close Modals which requires manually inserting the ReactNode.
 * Prefer the alternative, useSimpleModal.
 *
 */
export const useModal = <P extends ClosableProps>(
    C: React.ComponentType<P>,
): {
    Modal: ModalType<P>
    open: () => void
    close: () => void
    isOpen: boolean
} => {
    const [isOpen, setOpen] = useState(false)
    const { open, close } = useMemo(
        () => ({
            open: () => setOpen(true),
            close: () => setOpen(false),
        }),
        [setOpen],
    )
    const Modal = useMemo(() => {
        // assignment is just to get a named component
        const Wrapped: ModalType<P> = (props: Omit<P, keyof ClosableProps>) =>
            (isOpen ? createElement(C, { ...(props as P), close }) : null)
        Wrapped.displayName = `Wrapped${C.displayName || C.name || 'Modal'}`
        return Wrapped
    }, [C, isOpen, close])
    return { Modal, isOpen, open, close }
}
